/**
 * Code Agent Panel
 * AI-powered code generation assistant as an inline panel for Code nodes
 * Used as a tab in the NodeEditorModal left panel
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DroppableTextarea } from './DroppableTextarea';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  Bot,
  User,
  Send,
  Loader2,
  Copy,
  Check,
  Plus,
  Sparkles,
} from 'lucide-react';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
  chatWithCodeAgent,
  extractCodeBlocks,
  type CodeAgentMessage,
  type CodeAgentInputNode,
} from '@/services/codeAgentApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface InputData {
  nodeId: string;
  nodeName: string;
  nodeType?: string;
  nodeDistance?: number;
  data?: any;
  output?: any;
  sourceHandle?: string;
}

// Export ChatMessage type for external state management
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  codeBlocks?: Array<{ language: string; code: string }>;
}

interface CodeAgentPanelProps {
  language: 'javascript' | 'python';
  executionTier: 'sandboxed' | 'extended';
  inputData: InputData[];
  existingCode: string;
  onInsertCode: (code: string) => void;
  // Optional external state management for conversation persistence across tab switches
  externalMessages?: ChatMessage[];
  onMessagesChange?: (messages: ChatMessage[]) => void;
}

export function CodeAgentPanel({
  language,
  executionTier,
  inputData,
  existingCode,
  onInsertCode,
  externalMessages,
  onMessagesChange,
}: CodeAgentPanelProps) {
  const { getAccessToken } = useIdentity();
  const { subscriptionId } = useSubscription();

  // Use external state if provided, otherwise use internal state
  const [internalMessages, setInternalMessages] = useState<ChatMessage[]>([]);
  const messages = externalMessages ?? internalMessages;

  // Helper to update messages that works with both internal and external state
  const updateMessages = useCallback((updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    if (onMessagesChange) {
      // External state - need to resolve the updater function manually
      if (typeof updater === 'function') {
        const currentMessages = externalMessages ?? [];
        onMessagesChange(updater(currentMessages));
      } else {
        onMessagesChange(updater);
      }
    } else {
      setInternalMessages(updater);
    }
  }, [onMessagesChange, externalMessages]);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  // Focus textarea when panel mounts
  useEffect(() => {
    if (textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleSend = useCallback(async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmedInput,
      timestamp: new Date(),
    };
    updateMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setStreamingContent('');

    // Build conversation history
    const conversationMessages: CodeAgentMessage[] = [
      ...messages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content: trimmedInput },
    ];

    // Build input schema from inputData
    // The inputData contains unified format { success, output, logs, metrics }
    // Extract the inner 'output' which is the actual data the user will access via $node["Name"].output
    const inputSchema: CodeAgentInputNode[] = inputData.map((d) => {
      // Get the unified format object
      const unified = d.data || d.output;
      // Extract the inner output (actual data) - this matches what $node["Name"].output returns
      const actualOutput = unified?.output ?? unified;
      console.log('🤖 [CodeAgent] Building input schema for node:', {
        nodeName: d.nodeName,
        hasData: !!d.data,
        hasOutput: !!d.output,
        unifiedType: typeof unified,
        actualOutputType: typeof actualOutput,
        actualOutputPreview: actualOutput ? JSON.stringify(actualOutput).slice(0, 200) : null,
      });
      return {
        nodeId: d.nodeId,
        nodeName: d.nodeName,
        sampleData: actualOutput,
      };
    });

    console.log('🤖 [CodeAgent] Sending to API:', {
      inputSchemaLength: inputSchema.length,
      inputSchema: inputSchema.map(s => ({ nodeName: s.nodeName, hasSampleData: !!s.sampleData })),
      language,
      executionTier,
      hasExistingCode: !!existingCode,
    });

    // Start streaming request
    abortControllerRef.current = chatWithCodeAgent(
      {
        messages: conversationMessages,
        language,
        executionTier,
        inputSchema,
        existingCode: existingCode || undefined,
      },
      {
        getAccessToken,
        subscriptionId: subscriptionId || undefined,
        onChunk: (chunk) => {
          setStreamingContent((prev) => prev + chunk);
        },
        onComplete: (fullResponse) => {
          // Extract code blocks from response
          const codeBlocks = extractCodeBlocks(fullResponse);

          // Add assistant message
          const assistantMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: fullResponse,
            timestamp: new Date(),
            codeBlocks,
          };
          updateMessages((prev) => [...prev, assistantMessage]);
          setStreamingContent('');
          setIsLoading(false);
        },
        onError: (error) => {
          // Add error as assistant message
          const errorMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: `Sorry, I encountered an error: ${error}`,
            timestamp: new Date(),
          };
          updateMessages((prev) => [...prev, errorMessage]);
          setStreamingContent('');
          setIsLoading(false);
        },
      }
    );
  }, [
    inputValue,
    isLoading,
    messages,
    language,
    executionTier,
    inputData,
    existingCode,
    getAccessToken,
    subscriptionId,
    updateMessages,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleCopyCode = useCallback(async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCodeId(id);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } catch {
      // Ignore copy errors
    }
  }, []);

  const handleInsertCode = useCallback(
    (code: string) => {
      onInsertCode(code);
    },
    [onInsertCode]
  );

  // Detect dark mode
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const codeStyle = isDark ? vscDarkPlus : vs;

  return (
    <div className="flex flex-col h-full w-full bg-background min-w-0 overflow-hidden">
      {/* Messages Area */}
      <ScrollArea className="flex-1 w-full px-4 min-w-0" ref={scrollAreaRef}>
        <div className="py-4 space-y-4 min-w-0 w-full">
          {messages.length === 0 && !streamingContent && (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                Describe what you want your code to do
              </p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                I'll generate {language} code using your input data
              </p>
            </div>
          )}

          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              codeStyle={codeStyle}
              copiedCodeId={copiedCodeId}
              onCopyCode={handleCopyCode}
              onInsertCode={handleInsertCode}
            />
          ))}

          {/* Streaming indicator */}
          {streamingContent && (
            <div className="flex gap-3 min-w-0 w-full">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <Bot className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0 w-0 rounded-lg bg-muted/50 px-3 py-2 overflow-hidden">
                <div className="prose prose-sm dark:prose-invert max-w-full break-words overflow-hidden [&>pre]:max-w-full [&>pre]:overflow-x-auto text-sm [&>p]:text-sm [&>p]:leading-relaxed [&>p]:my-2 [&>ul]:text-sm [&>ol]:text-sm [&>li]:text-sm [&>strong]:text-sm">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {streamingContent}
                  </ReactMarkdown>
                </div>
                <Loader2 className="w-4 h-4 animate-spin mt-2" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t p-4 flex-shrink-0">
        <div className="flex gap-2">
          <DroppableTextarea
            ref={textareaRef}
            value={inputValue}
            onValueChange={setInputValue}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want... (drag fields from Input Data)"
            className="min-h-[60px] max-h-[120px] resize-none text-sm"
            disabled={isLoading}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            className="self-end h-9 w-9 p-0"
            size="sm"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Message Bubble Component
interface MessageBubbleProps {
  message: ChatMessage;
  codeStyle: any;
  copiedCodeId: string | null;
  onCopyCode: (code: string, id: string) => void;
  onInsertCode: (code: string) => void;
}

function MessageBubble({
  message,
  codeStyle,
  copiedCodeId,
  onCopyCode,
  onInsertCode,
}: MessageBubbleProps) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex gap-3 justify-end">
        <div className="max-w-[85%] rounded-lg bg-primary text-primary-foreground px-3 py-2">
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
          <User className="w-4 h-4 text-primary-foreground" />
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex gap-3 min-w-0 w-full">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
        <Bot className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0 w-0 space-y-2 overflow-hidden">
        <div className="rounded-lg bg-muted/50 px-3 py-2 w-full overflow-hidden">
          <div className="prose prose-sm dark:prose-invert max-w-full break-words overflow-hidden [&>pre]:max-w-full [&>pre]:overflow-x-auto text-sm [&>p]:text-sm [&>p]:leading-relaxed [&>p]:my-2 [&>ul]:text-sm [&>ol]:text-sm [&>li]:text-sm [&>strong]:text-sm">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Override pre to pass children through - prevents invalid <pre><div><button></div></pre> nesting
                pre({ children }) {
                  return <>{children}</>;
                },
                code({ className, children }: any) {
                  const content = String(children).replace(/\n$/, '');
                  const langMatch = /language-(\w+)/.exec(className || '');
                  const lang = langMatch ? langMatch[1] : '';

                  // Detect if this is a code block (has language) or inline code (no language, single line, short)
                  const isInlineCode = !lang && content.split('\n').length === 1 && content.length < 50;

                  if (isInlineCode) {
                    return (
                      <code className="px-1.5 py-0.5 bg-muted rounded text-[13px] font-mono">
                        {children}
                      </code>
                    );
                  }

                  const codeId = `${message.id}-${content.slice(0, 20)}`;

                  // Only show Insert button for actual code (javascript/python) with multiple lines
                  const isInsertableCode = (lang === 'javascript' || lang === 'python') &&
                    content.split('\n').length > 1;

                  return (
                    <div className="my-3 rounded-md border min-w-0 w-full overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-muted border-b flex-shrink-0">
                        <span className="text-xs font-medium text-muted-foreground uppercase">
                          {lang || 'code'}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => onCopyCode(content, codeId)}
                          >
                            {copiedCodeId === codeId ? (
                              <>
                                <Check className="w-3 h-3 mr-1" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 mr-1" />
                                Copy
                              </>
                            )}
                          </Button>
                          {isInsertableCode && (
                            <Button
                              variant="default"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => onInsertCode(content)}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Insert
                            </Button>
                          )}
                        </div>
                      </div>
                      <SyntaxHighlighter
                        language={lang || 'text'}
                        style={codeStyle}
                        customStyle={{
                          margin: 0,
                          fontSize: '13px',
                          padding: '1rem',
                          overflowX: 'auto',
                          maxWidth: '100%',
                        }}
                        showLineNumbers={content.split('\n').length > 3}
                      >
                        {content}
                      </SyntaxHighlighter>
                    </div>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
